import {
    allowResourceFromCatalog,
    createRoutePermissionResolver,
    resolveResourcePermission as resolveResourcePermissionFromCatalog,
    inferOperationFromPath,
} from './helpers';
import type { AllowResourceOptions } from './helpers';
import type {
    PermissionOperationHint,
    PermissionResourceCatalog,
    PermissionResourceKey,
    PermissionValue,
} from './types';

export interface AccessControlBindings<
    Catalog extends PermissionResourceCatalog<Value>,
    Value extends PermissionValue = PermissionValue
> {
    resolveResourcePermission: (
        resource: PermissionResourceKey<Catalog>,
        operation?: PermissionOperationHint
    ) => Value | undefined;
    resolveRoutePermission: (
        path: string,
        resource?: PermissionResourceKey<Catalog>,
        operationHint?: PermissionOperationHint
    ) => Value | undefined;
    allowResource: (resource: string, options?: AllowResourceOptions) => Value[];
    inferOperationFromPath: typeof inferOperationFromPath;
}

export function bindAccessControlCatalog<
    Catalog extends PermissionResourceCatalog<Value>,
    Permissions extends Record<string, Value>,
    Value extends PermissionValue = PermissionValue
>(permissions: Permissions, catalog: Catalog) {
    const resolveRoute = createRoutePermissionResolver<Catalog, Value>(catalog);

    const bindings = {
        resolveResourcePermission(resource: PermissionResourceKey<Catalog>, operation?: PermissionOperationHint) {
            return resolveResourcePermissionFromCatalog<Catalog, Value>(catalog, resource, operation);
        },
        resolveRoutePermission(path: string, resource?: PermissionResourceKey<Catalog>, operationHint?: PermissionOperationHint) {
            return resolveRoute(path, resource, operationHint);
        },
        allowResource(resource: string, options?: AllowResourceOptions) {
            return allowResourceFromCatalog(permissions, resource, options);
        },
        inferOperationFromPath,
    } satisfies AccessControlBindings<Catalog, Value>;

    return bindings;
}

export type { AllowResourceOptions };
